import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { ColumnIndexes, ColumnKeys, UpdateFiltersVisibilityRequest } from '../view/gridConfig/companies-grid-columns';
import {
  ColumnDetails,
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
} from '../../../grid/types/common';
import { GetCompaniesResponse } from '../types/companies-grid-responses';
import { Company } from '../types/companies-grid-company';
import { Filter } from '../../../grid/gridFilters/types';
import { convertGridFiltersToExtJsFilters, getColumnKeyForFilter, isDefaultFilter } from '../../../grid/utils/filters';
import { CompanyData } from '../../../company/types/company-data';
import { CompanyStatus } from '../../../company/types/company-status';
import { CompanyType } from '../../../company/types/company-type';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../grid/gridView/types';
import { ExtJsFilter } from '../../../xdr/responseGrid/types';

export class CompaniesGridDataProvider extends GridBaseDataProvider<ColumnIndexes, ColumnKeys> {
  private columnNameFrontendToGzBackend = {
    [ColumnKeys.COMPANY_NAME]: 'name',
    [ColumnKeys.COMPANY_TYPE]: 'specifics.rights.manageCompanies',
    [ColumnKeys.CAN_BE_MANAGED_BY_ABOVE]: 'specifics.canBeManagedByAbove',
    [ColumnKeys.PAYMENT_PLAN]: 'specifics.paymentPlan',
    [ColumnKeys.COMPANY_ID]: '_id',
    [ColumnKeys.LICENSE_KEY]: 'specifics.licenses.key',
    [ColumnKeys.EXPIRY_DATE]: 'specifics.licenses.expiryDate',
    [ColumnKeys.COMPANY_STATUS]: 'specifics.isSuspended',
    [ColumnKeys.PRODUCT_STATUS]: 'specifics.licenses.status',
    [ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS]: 'specifics.mspInProductTrialStatus',
  };
  private readonly companiesViewStateName = 'companies-companiesViewState';

  private cachedGetCompaniesResponse?: GetCompaniesResponse;
  private readonly allColumnDetails: {};

  constructor(public dependencies: any, allColumnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>[]) {
    super();
    this.allColumnDetails = allColumnDetails.reduce((acc, curr) => ({ ...acc, [curr.key]: curr }), {});
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    const pageNumber = request.pageNumber ?? DEFAULT_PAGE_NUMBER;
    const pageSize = request.pageSize ?? DEFAULT_PAGE_SIZE;
    const sortColumnName = request.sortColumnName ? this.columnNameFrontendToGzBackend[request.sortColumnName] : null;
    const sortDirection = request.sortDirection ?? null;
    const convertedFiltersToGzBackend = this.getConvertedFrontendFiltersToGzBackendFilters(request.filters);

    this.cachedGetCompaniesResponse = (await this.dependencies.api.clients.Companies.GetCompanies(
      pageNumber,
      pageSize,
      sortColumnName,
      sortDirection,
      convertGridFiltersToExtJsFilters(convertedFiltersToGzBackend)
    ).toPromise()) as GetCompaniesResponse;

    return {
      summary: '',
      numItems: this.cachedGetCompaniesResponse.total,
    };
  }

  async getRows(request: GetRowsRequest<ColumnIndexes, ColumnKeys>) {
    if (!this.cachedGetCompaniesResponse) {
      console.error('no cached GetCompanies response');
    }

    const convertedCompanies = this.convertCompaniesToGridFormat(this.cachedGetCompaniesResponse?.data ?? []);

    delete this.cachedGetCompaniesResponse;

    return {
      success: true,
      rawEvents: convertedCompanies,
    };
  }

  async updateSettings(request: UpdateSettingsRequest<ColumnIndexes>): Promise<{}> {
    this.saveViewState(request);
    return {};
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    this.saveViewState(request);
    return;
  }

  private saveViewState(
    request: SetFiltersRequest<ColumnKeys> | UpdateSettingsRequest<ColumnIndexes> | UpdateFiltersVisibilityRequest
  ): void {
    const newState = this.getViewState();

    for (const property in request) {
      if (request.hasOwnProperty(property) && request[property] !== undefined) {
        newState[property] = request[property];
      }
    }

    Ext.state.Manager.set(this.companiesViewStateName, newState);
  }

  public getViewState(): UpdateSettingsRequest<ColumnIndexes> &
    SetFiltersRequest<ColumnKeys> &
    UpdateFiltersVisibilityRequest {
    return Ext.state.Manager.get(this.companiesViewStateName, {}) as UpdateSettingsRequest<ColumnIndexes> &
      SetFiltersRequest<ColumnKeys> &
      UpdateFiltersVisibilityRequest;
  }

  private convertCompaniesToGridFormat(companiesData: CompanyData[]): Array<Company> {
    return companiesData.map(rawCompany => {
      const licenses = rawCompany.specifics.licenseUsageDetails ?? [];

      return {
        _id: rawCompany._id,
        companyName: rawCompany.name,
        companyType: rawCompany.companyType,
        companyStatus: rawCompany.specifics.isSuspended ? CompanyStatus.Suspended : CompanyStatus.Active,
        canBeManagedByAbove: rawCompany.specifics.canBeManagedByAbove ? 1 : 0,
        paymentPlan: rawCompany.specifics.paymentPlan,
        companyId: rawCompany._id,
        isSuspended: rawCompany.specifics.isSuspended,

        licenses: licenses,
        hasChildren: licenses.length > 1,
        subscription: rawCompany.subscription,
        unlicensed: rawCompany.specifics.unlicensed,
        mspInProductTrialStatus: rawCompany.specifics.mspInProductTrialStatus,
        edrMspInProductTrialDelegate: rawCompany.specifics.features.edrMspInProductTrialDelegate,
        mdrMspInProductTrialDelegate: rawCompany.specifics.features.mdrMspInProductTrialDelegate,
        edrMspInProductTrialVisible: rawCompany.specifics.features.edrMspInProductTrialVisible,
        mdrMspInProductTrialVisible: rawCompany.specifics.features.mdrMspInProductTrialVisible,
      };
    });
  }

  private getConvertedFrontendFiltersToGzBackendFilters(filters: Filter<ColumnKeys>[]): Filter<ColumnKeys>[] {
    const convertedFiltersToGzBackend: Filter<ColumnKeys>[] = [];

    for (const filter of filters) {
      const columnKey: ColumnKeys = getColumnKeyForFilter(filter);
      const columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys> = this.allColumnDetails[columnKey];

      if (!columnDetails) {
        console.error(`Missing column details for key: ${columnKey}`);
        continue;
      }

      if (isDefaultFilter(filter, columnDetails)) {
        continue;
      }

      convertedFiltersToGzBackend.push(this.getConvertedFrontendFilterToGzBackendFilter(filter, columnKey));
    }

    return convertedFiltersToGzBackend;
  }

  private getConvertedFrontendFilterToGzBackendFilter(
    filter: Filter<ColumnKeys>,
    columnKey: ColumnKeys
  ): Filter<ColumnKeys> {
    const filterValue = filter[columnKey];

    if (columnKey === ColumnKeys.COMPANY_TYPE && filterValue.in) {
      filterValue.in = filterValue.in.map(companyType => companyType === CompanyType.Partner);
    }

    if (columnKey === ColumnKeys.COMPANY_STATUS && filterValue.in) {
      filterValue.in = filterValue.in.map(companyStatus => companyStatus === CompanyStatus.Suspended);
    }

    return {
      [this.columnNameFrontendToGzBackend[columnKey] as ColumnKeys]: filterValue,
    };
  }

  public getConvertedFilters(filters: Filter<ColumnKeys>[]): Array<ExtJsFilter<ColumnKeys>> {
    return convertGridFiltersToExtJsFilters(this.getConvertedFrontendFiltersToGzBackendFilters(filters));
  }

  public updateFiltersVisibility(showFiltersRequest: UpdateFiltersVisibilityRequest) {
    this.saveViewState(showFiltersRequest);
  }

  shouldDataReloadOnResetView(): boolean {
    return true;
  }
}
